import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { 
  FileText, 
  Upload, 
  Download, 
  Plus, 
  Loader2,
  File,
  FileCheck,
  ExternalLink,
  Clock,
  Eye,
  Trash2,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default fallback document types (used if API fetch fails)
const DEFAULT_DOCUMENT_TYPES = [
  { value: 'assessment', label: 'Assessment' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'identification', label: 'Identification' },
  { value: 'individual_employment_plan', label: 'Individual Employment Plan' },
  { value: 'paystub', label: 'Paystub' },
  { value: 'referral_direct', label: 'Referral - Direct' },
  { value: 'referral_reverse', label: 'Referral - Reverse' },
  { value: 'timesheet', label: 'Timesheet' },
  { value: 'verification', label: 'Verification' },
  { value: 'other', label: 'Other' }
];

const CaseDocumentsPanel = ({ caseId, caseName, participantId, onDocumentAdded }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jotforms, setJotforms] = useState([]);
  const [documentTypes, setDocumentTypes] = useState(DEFAULT_DOCUMENT_TYPES);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [launchingForm, setLaunchingForm] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  
  // Edit document state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editForm, setEditForm] = useState({
    document_name: '',
    document_type: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    document_name: '',
    document_type: '',
    file: null,
    notes: ''
  });
  
  // Template form state
  const [templateForm, setTemplateForm] = useState({
    template_id: '',
    notes: ''
  });
  
  // Jotform state
  const [selectedJotform, setSelectedJotform] = useState('');

  useEffect(() => {
    if (caseId) {
      fetchDocuments();
      fetchTemplates();
      fetchJotforms();
      fetchDocumentTypes();
    }
  }, [caseId]);

  const fetchDocumentTypes = async () => {
    try {
      // Primary: fetch from unified dropdowns API
      const response = await axios.get(`${API}/dropdowns/document_types`);
      if (response.data.items && response.data.items.length > 0) {
        setDocumentTypes(response.data.items);
      }
    } catch (error) {
      console.error('Failed to fetch document types from dropdowns API:', error);
      // Fallback: try the legacy admin/document-types endpoint
      try {
        const legacyResponse = await axios.get(`${API}/admin/document-types`);
        if (legacyResponse.data.types && legacyResponse.data.types.length > 0) {
          // Convert legacy format to dropdown format
          const converted = legacyResponse.data.types
            .filter(t => t.is_active !== false)
            .map(t => ({ value: t.id, label: t.name }));
          setDocumentTypes(converted);
        }
      } catch (legacyError) {
        console.error('Failed to fetch document types from legacy API:', legacyError);
        // Keep default types if all fetches fail
      }
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cases/${caseId}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/document-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchJotforms = async () => {
    try {
      const response = await axios.get(`${API}/jotforms/available`);
      setJotforms(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch jotforms:', error);
    }
  };

  const handleLaunchJotform = async () => {
    if (!selectedJotform) {
      toast.error('Please select a form');
      return;
    }

    if (!participantId) {
      toast.error('Participant ID is required to launch the form');
      return;
    }

    setLaunchingForm(true);
    try {
      const response = await axios.post(`${API}/jotforms/launch`, {
        form_config_id: selectedJotform,
        participant_id: participantId,
        case_id: caseId
      });

      // Open the pre-filled form in a new window
      const formUrl = response.data.form_url;
      window.open(formUrl, '_blank', 'width=900,height=700,scrollbars=yes');
      
      toast.success(`Form "${response.data.form_name}" launched! Complete and submit the form.`);
      setShowAddDialog(false);
      setSelectedJotform('');
      
      // Note: The document will be automatically added via webhook when form is submitted
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to launch form');
    } finally {
      setLaunchingForm(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.document_name || !uploadForm.document_type) {
      toast.error('Please fill in document name and type');
      return;
    }

    if (!uploadForm.file) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('document_name', uploadForm.document_name);
      formData.append('document_type', uploadForm.document_type);
      formData.append('notes', uploadForm.notes || '');

      // Upload file to server
      await axios.post(`${API}/cases/${caseId}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Document uploaded successfully');
      setShowAddDialog(false);
      setUploadForm({ document_name: '', document_type: '', file: null, notes: '' });
      fetchDocuments();
      if (onDocumentAdded) onDocumentAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleTemplateSubmit = async () => {
    if (!templateForm.template_id) {
      toast.error('Please select a template');
      return;
    }

    const template = templates.find(t => t.id === templateForm.template_id);
    if (!template) {
      toast.error('Template not found');
      return;
    }

    // For templates with placeholder flag (not configured with Jotform yet)
    if (template.placeholder) {
      toast.info('This template is not yet configured with Jotform. Please contact your administrator.');
      return;
    }

    // In a real implementation, this would open the Jotform in an iframe
    // For now, show a message
    toast.info('Jotform integration requires configuration. The form would open here.');
    
    // Create a placeholder document entry
    setUploading(true);
    try {
      await axios.post(`${API}/cases/${caseId}/documents`, {
        document_name: template.name,
        document_type: template.document_type,
        creation_method: 'jotform_generated',
        template_id: template.id,
        notes: templateForm.notes || 'Generated from template'
      });

      toast.success('Document template submitted');
      setShowAddDialog(false);
      setTemplateForm({ template_id: '', notes: '' });
      fetchDocuments();
      if (onDocumentAdded) onDocumentAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit template');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/documents/${document.id}/download`);
      
      if (response.data.file_url) {
        // Create a hidden anchor element to trigger download to user's downloads folder
        const link = document.createElement ? document.createElement('a') : window.document.createElement('a');
        link.href = response.data.file_url;
        link.download = document.document_name || 'document';
        link.target = '_blank';
        document.body ? document.body.appendChild(link) : window.document.body.appendChild(link);
        link.click();
        document.body ? document.body.removeChild(link) : window.document.body.removeChild(link);
        toast.success(`Downloading: ${document.document_name}`);
      } else {
        toast.warning('No file available for download. This document may not have a file attached.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to download document';
      if (errorMsg.includes('not found') || errorMsg.includes('different environment')) {
        toast.error('Document file not found. It may have been uploaded in a different environment or deleted.');
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleView = (doc) => {
    // Navigate to document viewer page
    navigate(`/cases/${caseId}/documents/${doc.id}/view`);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.document_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/cases/${caseId}/documents/${doc.id}`);
      toast.success(`Document "${doc.document_name}" deleted`);
      fetchDocuments();
      if (onDocumentAdded) onDocumentAdded(); // Refresh parent data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  // Edit document handlers
  const handleEditClick = (doc) => {
    setEditingDocument(doc);
    setEditForm({
      document_name: doc.document_name || '',
      document_type: doc.document_type || '',
      notes: doc.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleEditCancel = () => {
    setShowEditDialog(false);
    setEditingDocument(null);
    setEditForm({ document_name: '', document_type: '', notes: '' });
  };

  const handleEditSave = async () => {
    if (!editForm.document_name.trim()) {
      toast.error('Document name is required');
      return;
    }
    if (!editForm.document_type) {
      toast.error('Document type is required');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/cases/${caseId}/documents/${editingDocument.id}`, {
        document_name: editForm.document_name.trim(),
        document_type: editForm.document_type,
        notes: editForm.notes || ''
      });
      
      if (response.data.changes && response.data.changes.length > 0) {
        toast.success(`Document details updated (${response.data.changes.length} field(s) changed)`);
      } else {
        toast.info('No changes detected');
      }
      
      setShowEditDialog(false);
      setEditingDocument(null);
      setEditForm({ document_name: '', document_type: '', notes: '' });
      fetchDocuments();
      if (onDocumentAdded) onDocumentAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document details');
    } finally {
      setSaving(false);
    }
  };

  // Check if user can edit documents (all users except read_only_auditor)
  const canEditDocuments = user?.role !== 'read_only_auditor';

  const getDocumentIcon = (method) => {
    return method === 'jotform_generated' ? FileCheck : File;
  };

  return (
    <Card className="card-shadow">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Case Documents
          </CardTitle>
          <CardDescription>
            Documents attached to this case. Documents are immutable once added.
          </CardDescription>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Document to Case</DialogTitle>
              <DialogDescription>
                Documents can only be added within a case context and become immutable once attached.
              </DialogDescription>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="upload">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </TabsTrigger>
                <TabsTrigger value="jotform">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Jotform
                </TabsTrigger>
                <TabsTrigger value="template">
                  <FileCheck className="w-4 h-4 mr-2" />
                  Template
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="upload" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Document Name *</Label>
                  <Input
                    value={uploadForm.document_name}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, document_name: e.target.value }))}
                    placeholder="e.g., Intake Assessment - January 2025"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Document Type *</Label>
                  <Select 
                    value={uploadForm.document_type}
                    onValueChange={(val) => setUploadForm(prev => ({ ...prev, document_type: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map(dt => (
                        <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>File</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files?.[0] }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Supported: PDF, Word, Excel, Images
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={uploadForm.notes}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about this document..."
                    rows={2}
                  />
                </div>
                
                <Button className="w-full" onClick={handleUpload} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Document
                </Button>
              </TabsContent>

              <TabsContent value="jotform" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Select Jotform *</Label>
                  <Select 
                    value={selectedJotform}
                    onValueChange={setSelectedJotform}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a form" />
                    </SelectTrigger>
                    <SelectContent>
                      {jotforms.length === 0 ? (
                        <SelectItem value="_none" disabled>No forms configured</SelectItem>
                      ) : (
                        jotforms.map(form => (
                          <SelectItem key={form.id} value={form.id}>
                            {form.name}
                            {form.is_required && ' (Required)'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {selectedJotform && jotforms.find(f => f.id === selectedJotform)?.description && (
                    <p className="text-xs text-muted-foreground">
                      {jotforms.find(f => f.id === selectedJotform)?.description}
                    </p>
                  )}
                </div>
                
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md text-sm">
                  <p className="font-medium text-blue-800 dark:text-blue-200">How Jotform works:</p>
                  <ol className="list-decimal list-inside mt-2 space-y-1 text-blue-700 dark:text-blue-300">
                    <li>Select a form from the dropdown</li>
                    <li>Form opens pre-filled with participant data</li>
                    <li>Complete and submit the form</li>
                    <li>PDF is <strong>automatically</strong> saved to this case</li>
                    <li>A case note is <strong>automatically</strong> created</li>
                  </ol>
                </div>
                
                {jotforms.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <p>No Jotforms have been configured.</p>
                    <p className="text-xs mt-1">Ask your administrator to set up forms in Administration → Jotforms</p>
                  </div>
                )}
                
                <Button 
                  className="w-full" 
                  onClick={handleLaunchJotform} 
                  disabled={launchingForm || !selectedJotform || jotforms.length === 0}
                >
                  {launchingForm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Launch Form
                </Button>
              </TabsContent>
              
              <TabsContent value="template" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Select Template *</Label>
                  <Select 
                    value={templateForm.template_id}
                    onValueChange={(val) => setTemplateForm(prev => ({ ...prev, template_id: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a document template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(tpl => (
                        <SelectItem key={tpl.id} value={tpl.id}>
                          {tpl.name}
                          {tpl.is_required_for_compliance && ' (Required)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {templateForm.template_id && (
                    <p className="text-xs text-muted-foreground">
                      {templates.find(t => t.id === templateForm.template_id)?.description}
                    </p>
                  )}
                </div>
                
                <div className="p-3 bg-muted rounded-md text-sm">
                  <p className="font-medium">How it works:</p>
                  <ol className="list-decimal list-inside mt-2 space-y-1 text-muted-foreground">
                    <li>Select a template from the dropdown</li>
                    <li>Complete the form (participant data is pre-filled)</li>
                    <li>Submit to generate a PDF</li>
                    <li>PDF is automatically attached to this case</li>
                  </ol>
                </div>
                
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={templateForm.notes}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Notes about this document..."
                    rows={2}
                  />
                </div>
                
                <Button className="w-full" onClick={handleTemplateSubmit} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck className="w-4 h-4 mr-2" />}
                  Start Form
                </Button>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : documents.length > 0 ? (
          <div className="space-y-3">
            {documents.map((doc) => {
              const IconComponent = getDocumentIcon(doc.creation_method);
              return (
                <div
                  key={doc.id}
                  className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{doc.document_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {documentTypes.find(dt => dt.value === doc.document_type)?.label || doc.document_type}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {doc.creation_method === 'jotform_generated' ? 'Template' : 'Upload'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(doc.created_at)} by {doc.created_by_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleView(doc)}
                        title="View document in new window"
                        data-testid={`view-document-${doc.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDownload(doc)}
                        title="Download document"
                        data-testid={`download-document-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {canEditDocuments && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEditClick(doc)}
                          title="Edit document details"
                          data-testid={`edit-document-${doc.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {canEditDocuments && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(doc)}
                          title="Delete document"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`delete-document-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No documents attached to this case</p>
            <p className="text-sm mt-1">Click &quot;Add Document&quot; to upload or create from template</p>
          </div>
        )}
      </CardContent>

      {/* Edit Document Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Document Details</DialogTitle>
            <DialogDescription>
              Update the document metadata. This will not replace the uploaded file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-document-name">Document Name *</Label>
              <Input
                id="edit-document-name"
                value={editForm.document_name}
                onChange={(e) => setEditForm({ ...editForm, document_name: e.target.value })}
                placeholder="Enter document name"
                data-testid="edit-document-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-document-type">Document Type *</Label>
              <Select 
                value={editForm.document_type} 
                onValueChange={(value) => setEditForm({ ...editForm, document_type: value })}
              >
                <SelectTrigger id="edit-document-type" data-testid="edit-document-type-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-document-notes">Notes</Label>
              <Textarea
                id="edit-document-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Additional notes about this document"
                rows={3}
                data-testid="edit-document-notes-input"
              />
            </div>
            
            {editingDocument && (
              <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                <p><strong>Original File:</strong> {editingDocument.original_filename || editingDocument.document_name}</p>
                <p><strong>Uploaded:</strong> {formatDateTime(editingDocument.created_at)}</p>
                <p><strong>Uploaded By:</strong> {editingDocument.created_by_name}</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={handleEditCancel}
              disabled={saving}
              data-testid="edit-document-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditSave}
              disabled={saving}
              data-testid="edit-document-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CaseDocumentsPanel;
